// The single imperative function you call from anywhere — socket handlers,
// async callbacks, store logic, plain utils, components. `toast` from
// react-hot-toast is a module-level singleton, so this needs no provider,
// context, or hook.
//
//   import { notify } from '@/core/toast'
//   notify('success', { title: 'You won', amount: '+₦545.00', meta: '5.09x' })
//
// JSX is intentionally avoided here (createElement) so this stays a .js file.
import { createElement } from 'react'
import toast from 'react-hot-toast'
import { logger } from '@logger'
import { TOAST_STATES } from '@constants/toastConstants'
import Toast from '@/components/toast/Toast'
import { playToastSound } from './toastSettings'

/**
 * Fire (or update) a toast.
 * @param {string} type one of: success | error | warning | info | loading
 * @param {object} options
 *   @param {string} [options.id]       reuse to update/resolve a live toast
 *                                      (e.g. loading -> success in place)
 *   @param {string} [options.title]    overrides the state default title
 *   @param {string} [options.sub]      secondary line
 *   @param {string} [options.amount]   pre-formatted, shown prominently (any value)
 *   @param {string} [options.meta]     small trailing label (e.g. a multiplier)
 *   @param {number} [options.duration] ms before auto-dismiss
 *   @param {string|false|null} [options.sound] app sound key override, or
 *                                      false/null to silence; omit for default
 *   @param {{label:string,onClick:()=>void}} [options.action] optional action
 *                                      button (e.g. Share); dismisses on click
 * @returns {string|undefined} the toast id (use with dismissToast / to update)
 */
export function notify(type, options = {}) {
  const cfg = TOAST_STATES[type]
  if (!cfg) {
    logger.warn('Toast', `Unknown toast type: "${type}"`)
    return
  }

  playToastSound(type, options.sound)

  return toast.custom((t) => createElement(Toast, { t, type, options }), {
    id: options.id, // stable id replaces/updates an existing toast
    duration: options.duration ?? cfg.duration,
  })
}

// Re-expose dismiss so callers never need to import react-hot-toast directly.
export const dismissToast = (id) => toast.dismiss(id)
